/**
 * The people in a workspace: Clerk's own screen, in our frame, with our invite form.
 *
 * Removing people, changing somebody's role, revoking an invitation and leaving are
 * Clerk's organisation profile, mounted rather than rebuilt. The membership already
 * lives in Clerk and the API already reads it out of every token, so a screen of our
 * own would be a second copy of Clerk's rules kept in step by hand.
 *
 * Inviting is the exception, and the reason is where the invitation's link goes.
 * Clerk's invite button cannot say, so its link uses the instance's one fallback
 * address — and one sign-in serves two products, so that address is the SDK's
 * account page. A colleague invited to a Cloud workspace would sign up and land on a
 * page about developer licences. The form here asks the Cloud API, which creates the
 * invitation with a redirect back to Cloud, and Clerk's button is hidden.
 *
 * Two other things are ours. `settleOrganisation` puts the workspace's rules onto the
 * organisation before Clerk's screen is shown at all: the plan's cap on people, and
 * no "Delete organization" button — which would remove the sign-in side of a
 * workspace and leave its subscription charging. If that cannot be done, the screen
 * is not shown. And a rename on Clerk's screen is carried back to the workspace,
 * because Clerk writes to Clerk, and the workspace's name is what deleting the
 * account asks to have typed.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api, helpUrl } from "../api.js";
import { clerk } from "../clerk.js";
import { Button, Field, Note } from "../ui/index.js";
import { useWorkspace } from "../workspace.js";

type State = { kind: "settling" } | { kind: "ready" } | { kind: "failed"; message: string };

export function People() {
  const { view, refresh } = useWorkspace();
  const [state, setState] = useState<State>({ kind: "settling" });
  // Bumped after an invitation is sent, so Clerk's list of invitations is drawn again with it in.
  const [generation, setGeneration] = useState(0);
  const mount = useRef<HTMLDivElement>(null);

  const settle = useCallback(async () => {
    setState({ kind: "settling" });
    try {
      await api.settleOrganisation();
      await refresh();
      setState({ kind: "ready" });
    } catch (e) {
      setState({ kind: "failed", message: e instanceof ApiError ? (e.hint ? `${e.message} ${e.hint}` : e.message) : "People can't be managed just now." });
    }
  }, [refresh]);

  useEffect(() => {
    void settle();
  }, [settle]);

  const ready = state.kind === "ready";

  useEffect(() => {
    const instance = clerk();
    const element = mount.current;
    if (!ready || !instance || !element) return;
    instance.mountOrganizationProfile(element, {
      // Hash routing, because the profile has pages of its own and this is a single
      // path in our router. Leaving the workspace ends on the home page, which then
      // asks which workspace to open.
      routing: "hash",
      afterLeaveOrganizationUrl: "/",
      // Invitations are sent from the form above; see the note at the top of the file.
      appearance: { elements: { membersPageInviteButton: { display: "none" } } }
    });
    let name = instance.organization?.name;
    const unsubscribe = instance.addListener(({ organization }) => {
      if (organization && organization.name !== name) {
        name = organization.name;
        void api
          .settleOrganisation()
          .then(() => refresh())
          .catch(() => {
            // The rename stands in Clerk; the next visit here carries it back.
          });
      }
    });
    return () => {
      unsubscribe?.();
      try {
        instance.unmountOrganizationProfile(element);
      } catch {
        // Already torn down by Clerk, which throws on a second unmount.
      }
    };
  }, [ready, refresh, generation]);

  const admin = view.role === "admin";

  return (
    <div className="wrap stack" style={{ gap: 24 }}>
      <div className="head">
        <div className="head__title">
          <span className="eyebrow">{view.workspace.name}</span>
          <h1>People</h1>
          <p className="head__lede">
            Everybody here can bring in data, design and make documents, and shares the workspace's limits.{" "}
            {admin ? "As an admin you can also invite and remove people, choose who else is an admin, and change the plan." : "Admins can also invite and remove people and change the plan."}
          </p>
        </div>
      </div>

      <Note tone="info">
        A workspace can have up to {view.limits.members} people. Invitations go by email, and somebody who already uses BroadPaper also finds the invitation in
        the workspace switcher at the top.{" "}
        <a href={helpUrl("/support/account/#people")} target="_blank" rel="noreferrer">
          How people and admins work
        </a>
      </Note>

      {state.kind === "failed" ? (
        <Note tone="bad" title="People can't be shown just now">
          {state.message}{" "}
          <Button size="sm" variant="quiet" onClick={() => void settle()}>
            Try again
          </Button>
        </Note>
      ) : state.kind === "settling" ? (
        <div className="empty">
          <span className="spinner spinner--lg" />
        </div>
      ) : admin ? (
        <Invite onSent={() => setGeneration((n) => n + 1)} />
      ) : null}

      {/* Always in the page, so the effect has somewhere to mount the moment it may. */}
      <div ref={mount} className="clerk-frame" hidden={!ready} />
    </div>
  );
}

function Invite({ onSent }: { onSent: () => void }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"member" | "admin">("member");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  const submit = async () => {
    const address = email.trim();
    if (!address) return setError("Enter the email address to send the invitation to.");
    setBusy(true);
    setError(null);
    setSent(null);
    try {
      await api.invite({ emailAddress: address, role });
      setSent(address);
      setEmail("");
      onSent();
    } catch (e) {
      setError(e instanceof ApiError ? (e.hint ? `${e.message} ${e.hint}` : e.message) : "The invitation couldn't be sent just now. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="card"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <div className="card__head">
        <strong>Invite somebody</strong>
      </div>
      <div className="card__body stack" style={{ gap: 12 }}>
        <div className="row" style={{ gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div className="grow" style={{ minWidth: 220 }}>
            <Field label="Email address" id="invite-email" {...(error ? { error } : {})}>
              <input
                id="invite-email"
                className="input"
                type="email"
                autoComplete="off"
                maxLength={320}
                value={email}
                disabled={busy}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError(null);
                }}
              />
            </Field>
          </div>
          <Field label="Role" id="invite-role">
            <select id="invite-role" className="select" value={role} disabled={busy} onChange={(e) => setRole(e.target.value as "member" | "admin")}>
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </Field>
          <Button type="submit" variant="primary" busy={busy}>
            Send invitation
          </Button>
        </div>
        {sent ? <Note tone="good">Invitation sent to {sent}. It is listed under Invitations below until it is accepted.</Note> : null}
      </div>
    </form>
  );
}
